import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Zap,
  Check,
  Flame,
  BookOpen,
  Lightbulb,
  Mail,
  Home as HomeIcon,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, IconChip } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { StatusGroup } from "@/components/status/status-button";
import { STATUS_SHAPES, STATUS_SHORT_LABELS, type StatusKey } from "@/lib/status";
import {
  ApiError,
  createBrightIdea,
  createEntry,
  getDashboard,
  getQuestions,
  getReflection,
  type CreatedEntry,
  type Question,
  type Reflection,
} from "@/lib/api";
import { greetingByHour } from "@/lib/format";

type Step =
  | "greeting"
  | "mood"
  | "question"
  | "journal"
  | "ideas"
  | "review"
  | "saving"
  | "complete";
type Answer = { status?: StatusKey; skipped?: boolean; note?: string };

const MOOD_LABELS: Record<StatusKey, string> = {
  green: "I'm doing well",
  yellow: "Something feels off",
  red: "I need help",
};

/** Client-side preview of the §6 roll-up (the server recomputes on save). */
function rollup(answers: Answer[]): StatusKey | null {
  const a = answers.filter((x) => !x.skipped && x.status);
  if (a.some((x) => x.status === "red")) return "red";
  if (a.some((x) => x.status === "yellow")) return "yellow";
  return a.length ? "green" : null;
}

export default function CheckIn() {
  const navigate = useNavigate();
  const goHome = () => navigate("/app");

  const [step, setStep] = useState<Step>("greeting");
  const [mode, setMode] = useState<"full" | "quick">("full");
  const [mood, setMood] = useState<StatusKey | undefined>();
  const [moodNote, setMoodNote] = useState("");

  const [allQuestions, setAllQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  const [qi, setQi] = useState(0);
  const [journal, setJournal] = useState("");
  const [idea, setIdea] = useState("");

  const [entry, setEntry] = useState<CreatedEntry | null>(null);
  const [streakDays, setStreakDays] = useState<number | null>(null);
  const [reflection, setReflection] = useState<Reflection | null>(null);
  const [reflectionPending, setReflectionPending] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getQuestions()
      .then(setAllQuestions)
      .catch((e) => {
        if (e instanceof ApiError && e.status === 401) navigate("/signin");
        else setError("Couldn't load your questions. Please try again.");
      });
  }, [navigate]);

  const questions = mode === "quick" ? allQuestions.filter((q) => q.is_key) : allQuestions;
  const total = questions.length;
  const answeredList = questions.map((q) => answers[q.id] ?? {});
  const overall = rollup(answeredList);

  function begin(chosen: "full" | "quick") {
    setMode(chosen);
    setMood(undefined);
    setMoodNote("");
    setAnswers({});
    setQi(0);
    setJournal("");
    setIdea("");
    setStep("mood");
  }

  const q = questions[qi];
  const setAnswer = (patch: Answer) =>
    setAnswers((prev) => ({ ...prev, [q.id]: { ...prev[q.id], ...patch } }));

  function advance(skip: boolean) {
    if (skip) setAnswer({ skipped: true, status: undefined });
    else setAnswer({ skipped: false });
    if (qi + 1 < total) setQi(qi + 1);
    else setStep(mode === "quick" ? "review" : "journal");
  }

  // progress bar percentage, roughly matching the design's pacing.
  const progress =
    step === "mood"
      ? 10
      : step === "question"
        ? 15 + ((qi + 1) / Math.max(total, 1)) * (mode === "quick" ? 65 : 50)
        : step === "journal"
          ? 72
          : step === "ideas"
            ? 84
            : 95;

  async function save() {
    setStep("saving");
    setError(null);
    try {
      const items = questions.map((qq) => {
        const a = answers[qq.id] ?? {};
        return {
          question_template_id: qq.source === "template" ? qq.id : null,
          custom_question_id: qq.source === "custom" ? qq.id : null,
          question_text: qq.question_text,
          status: a.skipped ? null : a.status ?? null,
          skipped: !!a.skipped,
          notes: a.note?.trim() || null,
        };
      });
      const created = await createEntry({
        mode,
        mood_status: mood ?? null,
        mood_note: moodNote.trim() || null,
        journal_text: mode === "full" ? journal.trim() || null : null,
        items,
      });
      if (mode === "full" && idea.trim()) {
        await createBrightIdea(idea.trim()).catch(() => {}); // best-effort
      }
      setEntry(created);
      setStep("complete");
      getDashboard()
        .then((d) => setStreakDays(d.streak.current))
        .catch(() => {});
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) navigate("/signin");
      else {
        setError("Something went wrong saving your check-in. Please try again.");
        setStep("review");
      }
    }
  }

  // Poll the async AI reflection once saved (§6 — the screen is already done).
  const pollRef = useRef<number | null>(null);
  useEffect(() => {
    if (step !== "complete" || !entry) return;
    let attempts = 0;
    const tick = async () => {
      attempts++;
      try {
        const r = await getReflection(entry.id);
        if (r) {
          setReflection(r);
          setReflectionPending(false);
          return;
        }
      } catch {
        /* keep trying */
      }
      if (attempts >= 6) {
        setReflectionPending(false);
        return;
      }
      pollRef.current = window.setTimeout(tick, 2500);
    };
    pollRef.current = window.setTimeout(tick, 1500);
    return () => {
      if (pollRef.current) window.clearTimeout(pollRef.current);
    };
  }, [step, entry]);

  /* ------------------------------ greeting ------------------------------ */
  if (step === "greeting") {
    return (
      <div className="flex min-h-dvh animate-fade-rise flex-col items-center justify-center px-6 text-center">
        <p className="text-lg text-muted-foreground">{greetingByHour()},</p>
        <p className="my-6 text-base text-muted-foreground">Ready for today's check-in?</p>
        <div className="flex w-full flex-col gap-3">
          <Button size="xl" fullWidth onClick={() => begin("full")}>
            Start Check-In
          </Button>
          <Button
            variant="ghost"
            fullWidth
            className="text-muted-foreground"
            onClick={() => begin("quick")}
          >
            <Zap className="h-4 w-4" strokeWidth={2} />
            Quick Check-In
          </Button>
          <Button variant="link" fullWidth className="text-[13px]" onClick={goHome}>
            Not right now
          </Button>
        </div>
        <p className="mt-6 text-xs text-muted-foreground">
          Full takes a few minutes. Quick is under a minute.
        </p>
      </div>
    );
  }

  const Header = ({ onBack }: { onBack: () => void }) => (
    <div className="flex items-center gap-3 px-6 pb-2 pt-6">
      <Button variant="ghost" size="icon" aria-label="Back" onClick={onBack}>
        <ArrowLeft className="h-5 w-5" strokeWidth={2} />
      </Button>
      <div className="flex-1">
        <Progress value={Math.round(progress)} />
      </div>
    </div>
  );

  /* -------------------------------- mood -------------------------------- */
  if (step === "mood") {
    return (
      <div className="animate-fade-rise">
        <Header onBack={() => setStep("greeting")} />
        <div className="px-6 pb-8 pt-6">
          <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
            Mood
          </p>
          <h2 className="mb-7 mt-1.5 text-xl font-semibold text-foreground">
            How are you feeling right now?
          </h2>
          <StatusGroup value={mood} onChange={setMood} />
          <div className="mt-5">
            <Textarea
              placeholder="Add a quick note (optional)"
              rows={3}
              value={moodNote}
              onChange={(e) => setMoodNote(e.target.value)}
            />
          </div>
          <Button
            size="xl"
            fullWidth
            className="mt-7"
            disabled={!mood || total === 0}
            onClick={() => {
              setQi(0);
              setStep("question");
            }}
          >
            Next
          </Button>
          {total === 0 && (
            <p className="mt-3 text-center text-xs text-muted-foreground">
              Loading your questions…
            </p>
          )}
        </div>
      </div>
    );
  }

  /* ------------------------------ question ------------------------------ */
  if (step === "question" && q) {
    const a = answers[q.id] ?? {};
    return (
      <div className="animate-fade-rise">
        <Header
          onBack={() => (qi > 0 ? setQi(qi - 1) : setStep("mood"))}
        />
        <div className="px-6 pb-8 pt-6">
          <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
            Question {qi + 1} of {total}
          </p>
          <h2 className="mb-7 mt-1.5 text-xl font-semibold text-foreground">
            {q.question_text}
          </h2>
          <StatusGroup
            value={a.skipped ? undefined : a.status}
            labels={q.labels}
            onChange={(status) => setAnswer({ status, skipped: false })}
          />
          <div className="mt-5">
            <Textarea
              placeholder="Add a quick note (optional)"
              rows={3}
              value={a.note ?? ""}
              onChange={(e) => setAnswer({ note: e.target.value })}
            />
          </div>
          <Button
            size="xl"
            fullWidth
            className="mt-7"
            disabled={!a.status || a.skipped}
            onClick={() => advance(false)}
          >
            Next
          </Button>
          <Button
            variant="link"
            fullWidth
            className="mt-2 text-[13px] text-muted-foreground"
            onClick={() => advance(true)}
          >
            Skip this question
          </Button>
        </div>
      </div>
    );
  }

  /* ------------------------------ journal ------------------------------- */
  if (step === "journal") {
    return (
      <div className="animate-fade-rise">
        <Header onBack={() => { setQi(total - 1); setStep("question"); }} />
        <div className="px-6 pb-8 pt-6">
          <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
            Journal
          </p>
          <h2 className="mt-1.5 text-xl font-semibold text-foreground">
            Anything on your mind?
          </h2>
          <p className="mb-5 mt-1 text-sm text-muted-foreground">
            A few honest lines are plenty. (optional)
          </p>
          <Textarea
            placeholder="Write freely — this stays private unless you share it."
            rows={8}
            value={journal}
            onChange={(e) => setJournal(e.target.value)}
          />
          <Button size="xl" fullWidth className="mt-7" onClick={() => setStep("ideas")}>
            Next
          </Button>
          <Button
            variant="link"
            fullWidth
            className="mt-2 text-[13px] text-muted-foreground"
            onClick={() => {
              setJournal("");
              setStep("ideas");
            }}
          >
            Skip for tonight
          </Button>
        </div>
      </div>
    );
  }

  /* ------------------------------- ideas -------------------------------- */
  if (step === "ideas") {
    return (
      <div className="animate-fade-rise">
        <Header onBack={() => setStep("journal")} />
        <div className="px-6 pb-8 pt-6">
          <div className="mb-2 flex items-center gap-3">
            <IconChip icon={Lightbulb} tint="primary" />
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
                Bright Ideas Box
              </p>
              <h2 className="mt-0.5 text-xl font-semibold text-foreground">
                Any bright ideas today?
              </h2>
            </div>
          </div>
          <p className="mb-5 mt-2 text-sm text-muted-foreground">
            Big plans, sudden decisions, exciting thoughts — drop them here to revisit
            with fresh eyes later.
          </p>
          <Textarea
            placeholder="e.g. Text my ex. Buy a motorcycle. Move to Portland."
            rows={4}
            value={idea}
            onChange={(e) => setIdea(e.target.value)}
          />
          <Button size="xl" fullWidth className="mt-7" onClick={() => setStep("review")}>
            {idea.trim() ? "Save Idea & Continue" : "Next"}
          </Button>
          <Button
            variant="link"
            fullWidth
            className="mt-2 text-[13px] text-muted-foreground"
            onClick={() => {
              setIdea("");
              setStep("review");
            }}
          >
            Nothing today
          </Button>
        </div>
      </div>
    );
  }

  /* ------------------------------- review ------------------------------- */
  if (step === "review") {
    const OverallShape = overall ? STATUS_SHAPES[overall] : null;
    return (
      <div className="animate-fade-rise pb-10">
        <Header
          onBack={() => {
            if (mode === "quick") {
              setQi(total - 1);
              setStep("question");
            } else setStep("ideas");
          }}
        />
        <div className="flex flex-col gap-3 px-6 pt-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
                Review{mode === "quick" ? " · Quick Mode" : ""}
              </p>
              <h2 className="mt-0.5 text-xl font-semibold text-foreground">
                Today's Inventory
              </h2>
            </div>
            {overall && OverallShape && (
              <Badge status={overall}>
                <OverallShape className="h-[11px] w-[11px]" strokeWidth={2} />
                {STATUS_SHORT_LABELS[overall]}
              </Badge>
            )}
          </div>

          {mood && (
            <Card>
              <CardContent className="flex items-center justify-between gap-3 px-4 py-3">
                <div>
                  <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
                    Mood
                  </p>
                  {moodNote && (
                    <p className="mt-0.5 text-[13px] text-muted-foreground">{moodNote}</p>
                  )}
                </div>
                <MoodBadge mood={mood} />
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="flex flex-col px-4 py-1">
              {questions.map((qq, i) => {
                const a = answers[qq.id] ?? {};
                const Shape = a.status && !a.skipped ? STATUS_SHAPES[a.status] : null;
                return (
                  <div
                    key={qq.id}
                    className={`flex items-center justify-between gap-3 py-2.5 ${
                      i < questions.length - 1 ? "border-b border-border" : ""
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-foreground">{qq.question_text}</p>
                      {a.note && (
                        <p className="mt-0.5 text-xs text-muted-foreground">{a.note}</p>
                      )}
                    </div>
                    {a.status && !a.skipped && Shape ? (
                      <Badge status={a.status}>
                        <Shape className="h-[11px] w-[11px]" strokeWidth={2} />
                        {STATUS_SHORT_LABELS[a.status]}
                      </Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">Skipped</span>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>

          {mode === "full" && journal.trim() && (
            <Card>
              <CardContent className="px-4 py-3">
                <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
                  Journal
                </p>
                <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-foreground">
                  {journal}
                </p>
              </CardContent>
            </Card>
          )}
          {mode === "full" && idea.trim() && (
            <Card>
              <CardContent className="flex items-start gap-2.5 px-4 py-3">
                <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-primary" strokeWidth={2} />
                <div>
                  <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
                    Bright Idea
                  </p>
                  <p className="mt-1 text-sm text-foreground">{idea}</p>
                </div>
              </CardContent>
            </Card>
          )}

          <Button size="xl" fullWidth className="mt-2" onClick={save}>
            Save Entry
          </Button>
          <p className="text-center text-xs text-muted-foreground">
            Original entries can't be edited — you can add revisions later.
          </p>
          {error && (
            <p className="text-center text-sm text-status-red-text">{error}</p>
          )}
        </div>
      </div>
    );
  }

  /* ------------------------------- saving ------------------------------- */
  if (step === "saving") {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-3 px-6 text-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" strokeWidth={2} />
        <p className="text-sm text-muted-foreground">Saving your check-in…</p>
      </div>
    );
  }

  /* ------------------------------ complete ------------------------------ */
  const displayStatus: StatusKey | null = entry?.overall_status ?? mood ?? null;
  const Shape = displayStatus ? STATUS_SHAPES[displayStatus] : null;
  return (
    <div className="flex min-h-dvh animate-fade-rise flex-col items-center justify-center gap-5 px-6 py-8 text-center">
      <div className="flex h-16 w-16 animate-scale-in items-center justify-center rounded-full bg-primary/10 text-primary">
        <Check className="h-8 w-8" strokeWidth={2.5} />
      </div>
      <div>
        <h1 className="text-2xl font-bold text-foreground">Check-In Complete</h1>
        <p className="mt-1 text-[15px] text-muted-foreground">You're aware for today.</p>
      </div>

      {displayStatus && Shape && (
        <Badge status={displayStatus} className="px-4 py-1 text-sm">
          <Shape className="h-3 w-3" strokeWidth={2} />
          {entry?.overall_status ? "Today" : "Mood"}: {STATUS_SHORT_LABELS[displayStatus]}
        </Badge>
      )}

      {entry?.overall_status === "red" && (
        <Card className="w-full border-status-red-bg text-left">
          <CardContent className="flex items-start gap-2.5 px-4 py-3">
            <Mail className="mt-0.5 h-4 w-4 shrink-0 text-status-red-text" strokeWidth={2} />
            <p className="text-[13px] leading-relaxed text-foreground">
              A quiet note went to your Sponsor and Trusted Contacts. Asking for help is
              part of the work.
            </p>
          </CardContent>
        </Card>
      )}

      {streakDays != null && streakDays > 0 && (
        <div className="flex items-center gap-2 text-foreground">
          <Flame className="h-5 w-5 text-status-yellow" strokeWidth={2} />
          <span className="text-[17px] font-semibold">
            {streakDays} Day{streakDays === 1 ? "" : "s"} Streak
          </span>
        </div>
      )}

      <Card className="w-full text-left">
        <CardContent className="flex flex-col gap-2.5 p-4">
          <p className="text-xs font-medium uppercase tracking-[0.05em] text-muted-foreground">
            Reflection
          </p>
          {reflection ? (
            <>
              <p className="text-sm leading-relaxed text-foreground">{reflection.content}</p>
              {reflection.reading_suggestion && (
                <div className="flex items-start gap-2 border-t border-border pt-2">
                  <BookOpen className="mt-0.5 h-4 w-4 shrink-0 text-primary" strokeWidth={2} />
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      {reflection.reading_suggestion}
                    </p>
                    {reflection.reading_reason && (
                      <p className="text-xs text-muted-foreground">
                        {reflection.reading_reason}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : reflectionPending ? (
            <p className="text-sm italic leading-relaxed text-muted-foreground">
              Writing a short reflection…
            </p>
          ) : (
            <p className="text-sm leading-relaxed text-muted-foreground">
              Your reflection will appear here shortly. You're all set for today.
            </p>
          )}
        </CardContent>
      </Card>

      <div className="flex w-full flex-col gap-3">
        <Button fullWidth onClick={goHome}>
          <HomeIcon className="h-4 w-4" strokeWidth={2} />
          Return to Dashboard
        </Button>
      </div>
    </div>
  );
}

function MoodBadge({ mood }: { mood: StatusKey }) {
  const Shape = STATUS_SHAPES[mood];
  return (
    <Badge status={mood}>
      <Shape className="h-[11px] w-[11px]" strokeWidth={2} />
      {MOOD_LABELS[mood]}
    </Badge>
  );
}
